import React, { useEffect, useMemo, useState } from 'react';

import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { allRegions } from '~/consts/allRegions';

import { useResolveRegionChannelSettings } from '~/hooks/apis/promo/useResolveRegionChannelSettings';

import type { RegionChannelSetting, ResolveRegionChannelSettingsQuery } from '~/services/PromoService';

const SCOPE_FIELDS = [
  { key: 'source', label: 'utm_source' },
  { key: 'medium', label: 'utm_medium' },
  { key: 'campaign', label: 'utm_campaign' },
  { key: 'term', label: 'utm_term' },
  { key: 'content', label: 'utm_content' },
] as const;

type ScopeForm = {
  region: string;
  source: string;
  medium: string;
  campaign: string;
  term: string;
  content: string;
};

const EMPTY_FORM: ScopeForm = { region: '', source: '', medium: '', campaign: '', term: '', content: '' };

const toQuery = (form: ScopeForm): ResolveRegionChannelSettingsQuery => ({
  region: form.region || undefined,
  source: form.source.trim() || undefined,
  medium: form.medium.trim() || undefined,
  campaign: form.campaign.trim() || undefined,
  term: form.term.trim() || undefined,
  content: form.content.trim() || undefined,
});

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' } as const;

/** Renders a stored pattern so a glob can never be mistaken for a literal value. */
function PatternValue({ value }: { value?: string | null }) {
  if (!value) {
    return (
      <Typography component="span" variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
        n/a
      </Typography>
    );
  }
  // A bare '*' falls out of this as a lone tinted asterisk — no special case needed.
  return (
    <Typography component="span" variant="body2" sx={mono}>
      {value.split('*').map((part, i, parts) => (
        <React.Fragment key={i}>
          {part}
          {i < parts.length - 1 && (
            <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
              *
            </Box>
          )}
        </React.Fragment>
      ))}
    </Typography>
  );
}

type Props = {
  /** Every active rule, so a resolved setting_id can be shown as the rule an operator recognises. */
  settings: Array<RegionChannelSetting>;
  labelForType: (type: string) => string;
};

export default function RegionRuleResolveTab({ settings, labelForType }: Props) {
  // The resolved customer lives in the URL, so a verdict can be linked to a colleague rather than
  // described. The form is a local draft; Resolve commits it.
  const [searchParams, setSearchParams] = useSearchParams();
  const scope: ScopeForm = useMemo(
    () => ({
      region: searchParams.get('region') ?? '',
      source: searchParams.get('source') ?? '',
      medium: searchParams.get('medium') ?? '',
      campaign: searchParams.get('campaign') ?? '',
      term: searchParams.get('term') ?? '',
      content: searchParams.get('content') ?? '',
    }),
    [searchParams],
  );

  const [form, setForm] = useState<ScopeForm>(scope);
  useEffect(() => setForm(scope), [scope]);

  const query = useMemo(() => toQuery(scope), [scope]);
  const { data, isFetching, isError, dataUpdatedAt, refetch } = useResolveRegionChannelSettings({ query });

  const commit = (next: ScopeForm) => {
    const params = new URLSearchParams(searchParams);
    (Object.keys(next) as Array<keyof ScopeForm>).forEach((key) => {
      const value = next[key].trim();
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    setSearchParams(params);
  };

  const byId = useMemo(() => new Map(settings.map((s) => [s.region_channel_setting_id, s])), [settings]);

  const set = (key: keyof ScopeForm) => (value: string) => setForm((f) => ({ ...f, [key]: value }));

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '72ch' }}>
          Enter a customer&apos;s region and UTMs to see which rule wins. This calls the same endpoint the customer
          portal calls, so the answer is what a real visitor would get.
        </Typography>
      </Box>

      <Alert severity="info">
        <Typography variant="body2">
          Answers are cached for <strong>up to 10 minutes</strong>. So this table doesn't always show the latest
          changes. Check it in 10min after you modify a rule.
        </Typography>
      </Alert>

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
            }}
          >
            <TextField
              select
              size="small"
              label="Region"
              value={form.region}
              onChange={(e) => set('region')(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {allRegions.map((r) => (
                <MenuItem key={r.code} value={r.code}>
                  {r.name} ({r.code})
                </MenuItem>
              ))}
            </TextField>
            {SCOPE_FIELDS.map((f) => (
              <TextField
                key={f.key}
                size="small"
                label={f.label}
                value={form[f.key]}
                onChange={(e) => set(f.key)(e.target.value)}
                inputProps={{ style: mono, spellCheck: false }}
              />
            ))}
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button variant="contained" onClick={() => commit(form)} disabled={isFetching}>
              Resolve
            </Button>
            <Button onClick={() => commit(EMPTY_FORM)} disabled={isFetching}>
              Clear
            </Button>
            <Button onClick={() => void refetch()} disabled={isFetching}>
              Re-check
            </Button>
            {isFetching && <CircularProgress size={18} />}
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.disabled">
              This customer is in the URL — copy it to share the verdict.
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {isError && <Alert severity="error">Failed to resolve. Check the region code and try again.</Alert>}

      {data && !isFetching && (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.09em', color: 'text.secondary' }}>
              Result
            </Typography>
            <Typography variant="caption" color="text.disabled">
              fetched {new Date(dataUpdatedAt).toLocaleTimeString()}
            </Typography>
          </Stack>

          {data.result.map((resolution) => {
            const winner = resolution.setting_id ? byId.get(resolution.setting_id) : undefined;
            return (
              <Paper
                key={resolution.setting_type}
                variant="outlined"
                sx={{
                  p: 2,
                  borderLeft: 4,
                  borderLeftColor: resolution.enabled ? 'success.main' : 'divider',
                }}
              >
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {labelForType(resolution.setting_type)}
                    </Typography>
                    <Chip
                      size="small"
                      color={resolution.enabled ? 'success' : 'default'}
                      label={resolution.enabled ? 'Enabled' : 'Disabled'}
                    />
                    {resolution.end_datetime && (
                      <Typography variant="caption" color="text.secondary" sx={mono}>
                        until {new Date(resolution.end_datetime).toISOString().slice(0, 10)}
                      </Typography>
                    )}
                  </Stack>

                  {!resolution.setting_id ? (
                    <Typography variant="body2" color="text.secondary">
                      No rule matches this customer — the setting defaults to off.
                    </Typography>
                  ) : (
                    <>
                      <Divider flexItem />
                      {/* Identity first, then what it matched on — keeping the rule id on its own
                          line stops it reading as the value of the last utm field. */}
                      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="caption" color="text.secondary">
                          Winning rule
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={winner?.region_code ? `${winner.region_code} region` : 'Global'}
                        />
                        <Link
                          component={RouterLink}
                          to={`/marketing/region-rule?id=${resolution.setting_id}`}
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12.5 }}
                        >
                          <Box component="span" sx={mono}>
                            {resolution.setting_id.slice(0, 8)}
                          </Box>
                          <OpenInNewIcon sx={{ fontSize: 13 }} />
                        </Link>
                      </Stack>

                      {winner ? (
                        <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap>
                          {SCOPE_FIELDS.map((f) => (
                            <Stack key={f.key} direction="row" spacing={0.75} alignItems="baseline">
                              <Typography variant="caption" color="text.disabled">
                                {f.label}
                              </Typography>
                              <PatternValue value={winner[f.key]} />
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          This rule is no longer in the active list — it may have just been changed, or the cached
                          answer above is out of date.
                        </Typography>
                      )}
                    </>
                  )}

                  {resolution.excluded_offer_ids && resolution.excluded_offer_ids.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {resolution.excluded_offer_ids.length} offer(s) excluded from this bypass regardless of channel.
                    </Typography>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
